// Scope (provider + profile) — fonte única de verdade pra isolamento de cache.
//
// Todas as camadas de cache (local-db, vod-info-store, series-info-store,
// React Query, settings persistido) usam o MESMO scope key pra garantir
// que dados de um (provider, profile) nunca vazam pra outro.
//
// Estrutura: `${providerPart}::${profilePart}` onde:
//   - providerPart é `p:<code>` (login com código) ou `d:<listName>` (lista direta)
//   - profilePart é `pf:<id>` ou `pf:_main` (sem perfil ativo / login fresh)
//
// Por que assim:
// - Trocar de provider → scope diferente → caches isolados ✓
// - Trocar de perfil dentro do mesmo provider → scope diferente → caches isolados ✓
// - Sem perfil ativo (login mas antes de escolher) → `_main` default,
//   permite gravar dados antes do user escolher perfil. Quando escolhe,
//   se mantém em `_main` ou migra (decisão do produto — hoje fica em `_main`).
//
// Catálogo (snapshot) NÃO usa este scope: catálogo é compartilhado entre
// perfis do mesmo provider (mesmas séries/filmes disponíveis). Snapshot
// continua scopado só por providerKey.

import { useSessionStore } from './store'

const PROFILE_FALLBACK = '_main'

/** Parte do scope que identifica o provider. null se logged out. */
export function providerPart(): string | null {
  const s = useSessionStore.getState()
  if (s.providerCode) return `p:${s.providerCode}`
  if (s.direct) return `d:${s.direct.listName}`
  return null
}

/** Parte do scope que identifica o perfil ativo. */
export function profilePart(): string {
  const s = useSessionStore.getState()
  return `pf:${s.activeProfileId ?? PROFILE_FALLBACK}`
}

/**
 * Scope completo (provider + profile). Síncrono, sem hook.
 * Retorna null quando não há provider ativo (logged out).
 *
 * Forma: `p:CODE::pf:ID` ou `d:LIST::pf:ID`. Sem perfil ativo, ID=`_main`.
 */
export function currentScope(): string | null {
  const provider = providerPart()
  if (!provider) return null
  // Contexto de CONTA (hidratado pela conta Swipe, `contextOwner` presente) sem perfil
  // escolhido: NÃO há escopo. O `pf:_main` não sincroniza, e gravar nele era perder o
  // que se assiste em silêncio (o buraco que o ProfileGuard fechou na TV, e que no
  // celular ficava aberto quando o pull falhava ou todo perfil tinha PIN). Sem escopo, o
  // save é no-op e a leitura vem vazia até o usuário escolher o perfil. A base sem conta
  // (dono nulo) segue no `_main` como sempre.
  const s = useSessionStore.getState()
  if (s.contextOwner !== null && !s.activeProfileId) return null
  return `${provider}::${profilePart()}`
}

/**
 * Snapshot key — só providerPart. Catálogo é compartilhado entre perfis,
 * então não inclui profilePart. Mantém compatibilidade com catalog-snapshot.ts
 * que usa providerKey "cru" (sem prefixo `p:`/`d:`).
 *
 * Retorna `providerCode` ou `direct:<listName>` (mesmo formato anterior).
 */
export function snapshotKey(): string | null {
  const s = useSessionStore.getState()
  if (s.providerCode) return s.providerCode
  if (s.direct) return `direct:${s.direct.listName}`
  return null
}

/**
 * Compõe key de cache prefixada pelo scope completo. Retorna null se
 * logged out (caller decide como tratar — geralmente no-op).
 *
 * Use sempre que gravar/ler de IDB ou cache em memória que precisa
 * isolamento por (provider, profile).
 */
export function scopedKey(rawId: string): string | null {
  const scope = currentScope()
  if (!scope) return null
  return `${scope}::${rawId}`
}
